import { Params, StateSchema } from '../helpers/types';
import { cmsQueryParamsPrefix } from '../routing';

export const keyTransformer = {
  parse: (key: string) => key.replace(cmsQueryParamsPrefix, ''),
  transform: (key: string, options?: { skipCmsPrefix?: boolean }) =>
    `${options?.skipCmsPrefix ? '' : cmsQueryParamsPrefix}${key}`,
};

export function parseQueryParams<T extends Params>(
  stateSchema: StateSchema<T>,
  queryParams: Iterable<[string, string]> | null,
): Partial<T> {
  const parsedParams: Record<string, unknown> = {};

  if (!queryParams) {
    return {};
  }

  for (const [originalKey, value] of queryParams) {
    const key = keyTransformer.parse(originalKey);
    const initialValue = stateSchema[key];

    if (initialValue === 'boolean') {
      parsedParams[key] = value === 'true';
    } else if (initialValue === 'number') {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        console.warn(
          `Expected a number for query param "${key}", but got "${value}". Defaulting to 0.`,
        );
        parsedParams[key] = value;
        continue;
      }

      parsedParams[key] = numValue;
    } else if (initialValue === 'string[]') {
      const parsedVal = value.split(',').filter((v) => v && v.length > 0);

      parsedParams[key] = [
        ...((parsedParams[key] as string[]) || []),
        ...parsedVal,
      ];
    } else {
      parsedParams[key] = value;
    }
  }

  return parsedParams as Partial<T>;
}

export function parseHashParams(hash: string): Record<string, string> {
  if (!hash) {
    return {};
  }

  const hashItems = hash
    .substring(1) // Remove the leading '#'
    .split('&')
    .map<[string, string]>((item) => item.split('=') as [string, string]);

  const dict: Record<string, string> = {};

  hashItems.forEach(([key, value]) => {
    dict[key] = decodeURIComponent(value ?? '');
  });

  return dict;
}

/**
 * `link` with its query edited. The query stays before the anchor: after it,
 * the browser reads both as one anchor — no scroll, and the query is lost.
 */
export function editLinkQuery(
  link: string,
  edit: (params: URLSearchParams, path: string) => void,
): string {
  const hashAt = link.indexOf('#');
  const anchor = hashAt === -1 ? '' : link.slice(hashAt);
  const [path, query] = (hashAt === -1 ? link : link.slice(0, hashAt)).split(
    '?',
  );
  const params = new URLSearchParams(query);
  edit(params, path);
  const search = params.toString();
  return `${path}${search ? `?${search}` : ''}${anchor}`;
}

export function encodeHashParams(
  hashState: Record<string, Params[string] | null>,
): string {
  const hashSet = {} as Record<string, string>;

  Object.entries(hashState).forEach(([originalKey, value]) => {
    const key = keyTransformer.transform(originalKey);

    if (value === undefined || value === null) {
      return;
    } else if (Array.isArray(value)) {
      hashSet[key] = value.join(',');
    } else {
      hashSet[key] = String(value);
    }
  });

  const newHash = Object.entries(hashSet)
    .map(([key, value]) =>
      value ? `${key}=${encodeURIComponent(value)}` : key,
    )
    .join('&');

  if (newHash) {
    return `#${newHash}`;
  }

  return '#';
}
